<script lang="ts">
    import { PREVIEW_CONTAINER_ID } from '$lib/global/constants';
    import { PREVIEW_MODE } from '$lib/global/constants';

    import { afterNavigate } from '$app/navigation';
    import Block from '$lib/components/Blocks/Block.svelte';
    import { NextPageNavigator } from '$lib/global/helper/NextPageNavigator';
    import { trackVariant } from '$lib/global/tracking';
    import { getTrackingService } from '$lib/global/tracking/TrackingServiceInstance';
    import { startPageReveal, stopPageReveal } from '$lib/Stores/blockReveal';
    import { heroImageKey, resolveHeroImageKey } from '$lib/Stores/imagePriority';
    import { createPageGuard } from '$lib/utils/createPageGuard';
    import { handleScrollFromUrl } from '$lib/utils/handleScrollFromUrl';
    import { isFunBrandsEnabled } from '$lib/utils/isFunBrandsEnabled';

    import { polyfillCountryFlagEmojis } from 'country-flag-emoji-polyfill';
    import debug from 'debug';
    import unescape from 'lodash/unescape';
    import { onMount, afterUpdate, onDestroy } from 'svelte';

    import type { Page } from '$lib/types/Page';
    import type { ComponentType } from 'svelte';

    export let pageData: Page;

    const debugLog = debug('page');

    const resolveDisplayData = (page: Page): Page => {
        const resolvedData = getTrackingService().resolveDisplayData(page);

        if (isFunBrandsEnabled(resolvedData)) {
            resolvedData.theme = resolvedData.themeV2;
        }

        return resolvedData;
    };

    let dataToDisplay = resolveDisplayData(pageData);
    let Confetti: ComponentType;

    // Mark the page's hero image (first image block in document order) as the LCP
    // priority image before any Block renders. Runs on init and whenever the page
    // changes, so the eager/fetchpriority="high" <img> always targets the real
    // hero and stays in sync with the publisher's hero preload tag.
    $: heroImageKey.set(resolveHeroImageKey(dataToDisplay.components));

    // CAP-4418: Arm the reveal hold for the page's blocks (see
    // `$lib/Stores/blockReveal`). Keyed on the page id rather than on
    // `dataToDisplay`, which `afterUpdate` reassigns on every update — restarting
    // the hold on each of those would keep the lower page hidden indefinitely.
    let heldPageId: string | undefined = undefined;

    const holdPageReveal = (page: Page) => {
        if (page.pageId === heldPageId) {
            return;
        }

        heldPageId = page.pageId;
        startPageReveal(page.components);
    };

    $: holdPageReveal(dataToDisplay);

    const pageGuard = createPageGuard();
    let redirectTimeout: ReturnType<typeof setTimeout> | undefined = undefined;

    debugLog('-> initial dataToDisplay', dataToDisplay);

    afterNavigate((nav) => {
        debugLog('-> afterNavigate', nav.type, nav.from?.url?.pathname, '→', nav.to?.url?.pathname);

        if (pageGuard(dataToDisplay.pageId)) {
            return;
        }

        // Run tracking side effects (page view, variant tracking)
        getTrackingService().trackPageTransition(dataToDisplay);

        const variant = getTrackingService().variant;

        if (variant && pageData.variantPageData) {
            const label = variant === 'A' ? 'Original' : 'Variant';
            trackVariant(`${label} - ${dataToDisplay.pageName}`);
        }

        // Handle scroll-to-component from URL parameters
        void handleScrollFromUrl();

        // Scrolls to top in preview mode after page navigation
        if (PREVIEW_MODE) {
            const previewContainerElement = document.getElementById(PREVIEW_CONTAINER_ID);

            if (previewContainerElement) {
                debugLog('-> Trigger reflow of "#perspective-frame-container" element');

                previewContainerElement.style.display = 'none';
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                previewContainerElement.offsetHeight; // Trigger reflow
                previewContainerElement.style.display = '';
                previewContainerElement.scrollTop = 0;
            }
        }

        // Inject custom head scripts
        if (dataToDisplay.headScript && !PREVIEW_MODE) {
            const codeSnippet = unescape(dataToDisplay.headScript);

            const parser = new DOMParser();
            const parsedSnippet = parser.parseFromString(codeSnippet, 'text/html');

            const invalidElements = Array.from(parsedSnippet.body.children).filter(
                (node) => node.tagName !== 'SCRIPT' && node.tagName !== 'STYLE',
            );

            if (invalidElements.length > 0) {
                console.error('Invalid HTML in code snippet.');
            } else {
                const range = document.createRange();
                const fragment = range.createContextualFragment(codeSnippet.trim());
                document.head.appendChild(fragment);
            }
        }
    });

    onMount(() => {
        debugLog('-> onMount');
        polyfillCountryFlagEmojis();
    });

    onDestroy(stopPageReveal);

    afterUpdate(async () => {
        // this is needed to make sure the page data is updated and this component re-renders
        dataToDisplay = resolveDisplayData(pageData);
        debugLog('-> afterUpdate dataToDisplay', dataToDisplay);

        if (dataToDisplay.animation === 'confetti' && !Confetti) {
            // CAUTION: dynamic import since it's not used on every page
            // -> this causes to trigger `afterUpdate` again!
            Confetti = (await import('$lib/components/Confetti/Confetti.svelte')).default;
        }

        // Automatic redirect handling
        if (redirectTimeout) {
            // Clear eventual timeout from previous page
            clearTimeout(redirectTimeout);
        }

        if (dataToDisplay.automaticRedirect?.enabled) {
            const nextPageNavigator = new NextPageNavigator(
                dataToDisplay.campaignId,
                dataToDisplay.firstResultPage,
                dataToDisplay.pageIdSlugMap,
                dataToDisplay.trackingProperties,
            );

            const { duration, linking } = dataToDisplay.automaticRedirect;

            redirectTimeout = setTimeout(() => {
                if (linking.target === 'external' && linking.url) {
                    return nextPageNavigator.goExternal(linking.url);
                }

                if (linking.target === 'next') {
                    return nextPageNavigator.go(dataToDisplay.nextPage);
                }

                // target is a pageId
                return nextPageNavigator.go(linking.target);
            }, duration * 1000);
        }
    });
</script>

<div class="fun-relative fun-z-10">
    {#key dataToDisplay.pageId}
        {#each dataToDisplay.components as component, index (component._id)}
            <Block
                id={component._id}
                language={dataToDisplay.language}
                campaignId={dataToDisplay.campaignId}
                campaignVersionId={dataToDisplay.campaignVersionId}
                companyId={dataToDisplay.companyId}
                cookiesEnabled={dataToDisplay.cookiesEnabled}
                componentType={component.componentType}
                childComponents={component.components}
                content={component.content}
                theme={dataToDisplay.theme}
                nextPage={dataToDisplay.nextPage}
                currentPage={dataToDisplay.pageId}
                resultMapping={dataToDisplay.resultMapping}
                fields={dataToDisplay.fields}
                firstResultPage={dataToDisplay.firstResultPage}
                trackingProperties={dataToDisplay.trackingProperties}
                {index}
            />
        {/each}
    {/key}
</div>

{#if dataToDisplay.animation === 'confetti'}
    <svelte:component this={Confetti} />
{/if}

{#if dataToDisplay.bodyScript && !PREVIEW_MODE}
    {@html unescape(dataToDisplay.bodyScript)}
{/if}
